import { type JSX, useCallback, useRef, useState } from 'react';
import { FormProvider, useForm, type DefaultValues, type FieldErrors, type FieldPath } from 'react-hook-form';
import { _get, _isNil, _notNil } from '@/littledash';
import type { VerticalStepFormProps } from './VerticalStepForm.model';
import { StepFormEngine } from './VerticalStepFormEngine';
import { flattenSelectValues } from './VerticalStepForm.utils';
import { StepFormProvider } from './VerticalStepFormContext';
import FormShell from './FormShell/FormShell';
import StepSection from './StepSection/StepSection';

/**
 * Config-driven vertical step form. Renders sequential collapsible steps,
 * each containing fields resolved from a registry. Manages progression,
 * validation, and submission via an internal StepFormEngine.
 */
const VerticalStepForm = <T extends Record<string, unknown>>({
  steps,
  defaultValues,
  onSubmit,
  title,
  description,
  submitDescription,
}: VerticalStepFormProps<T>): JSX.Element => {
  const engineRef = useRef(new StepFormEngine(steps));
  const formMethods = useForm<T>({
    defaultValues: defaultValues as DefaultValues<T>,
    mode: 'onTouched',
    shouldUnregister: false,
  });
  const { handleSubmit, trigger, resetField, getValues } = formMethods;
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The id of the step whose async action is in flight (null when none) — drives the Next button's
  // pending state so a slow round-trip can't be double-triggered.
  const [advancingStepId, setAdvancingStepId] = useState<string | null>(null);

  const handleAdvance = useCallback(
    async (stepId: string): Promise<void> => {
      const step = steps.find((s) => s.id === stepId);
      const stepFields = step?.fields.map((f) => f.name as FieldPath<T>) ?? [];
      const valid = stepFields.length > 0 ? await trigger(stepFields) : true;
      if (!valid) {
        return;
      }
      // No action: advance synchronously, exactly as before.
      if (_isNil(step?.action)) {
        engineRef.current.advance(stepId);
        return;
      }
      // Action present: run the round-trip; only advance if it resolves true. A false result or a
      // throw holds on this step (the action owns surfacing its own error to the user).
      setAdvancingStepId(stepId);
      try {
        const advanced = await step.action(getValues() as Record<string, unknown>);
        if (advanced) {
          engineRef.current.advance(stepId);
        }
      } finally {
        setAdvancingStepId(null);
      }
    },
    [steps, trigger, getValues]
  );

  const handleSkip = useCallback(
    (stepId: string): void => {
      const step = steps.find((s) => s.id === stepId);
      step?.fields.forEach((field) => resetField(field.name as FieldPath<T>));
      engineRef.current.skip(stepId);
    },
    [steps, resetField]
  );

  const handleGoToStep = useCallback((stepId: string): void => {
    engineRef.current.goToStep(stepId);
  }, []);

  const handleValueChange = useCallback((stepId: string): void => {
    engineRef.current.uncomplete(stepId);
  }, []);

  const handleFormSubmit = useCallback(
    async (values: T): Promise<void> => {
      setIsSubmitting(true);
      try {
        await onSubmit(flattenSelectValues(values, steps));
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, steps]
  );

  const onSubmitError = useCallback(
    (errors: FieldErrors<T>): void => {
      const firstErrorStep = steps.find((step) => step.fields.some((field) => _notNil(_get(errors, field.name))));
      if (firstErrorStep) {
        engineRef.current.goToStep(firstErrorStep.id);
      }
    },
    [steps]
  );

  return (
    <StepFormProvider value={{ engine: engineRef.current }}>
      <FormProvider {...formMethods}>
        <FormShell
          title={title}
          description={description}
          steps={steps}
          submitDescription={submitDescription}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit(handleFormSubmit, onSubmitError)}
        >
          {steps.map((step, index) => (
            <StepSection
              key={step.id}
              config={step}
              stepNumber={index + 1}
              isAdvancing={advancingStepId === step.id}
              onAdvance={handleAdvance}
              onSkip={handleSkip}
              onGoToStep={handleGoToStep}
              onValueChange={handleValueChange}
            />
          ))}
        </FormShell>
      </FormProvider>
    </StepFormProvider>
  );
};

export default VerticalStepForm;
